import type { Order } from '../types';
import { orderCreatedAtMs, toMillis } from './orderTimestamps';

/** Valid wrap size 2–9999; 0 / null / absent = no wrap (also persisted `0` = disabled). */
export function sanitizeEventTokenRollMax(raw: unknown): number | undefined {
  if (raw == null || raw === '' || raw === 0 || raw === '0') return undefined;
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 2 || n > 9999) return undefined;
  return n;
}

/**
 * Map monotonic sequence index to physical slip (1…rollMax), or pass through when roll is disabled.
 */
export function physicalSlipFromSequence(sequenceOneBased: number, rollMax?: number): number {
  const seq = Math.max(1, Math.floor(Number(sequenceOneBased)) || 1);
  const max = sanitizeEventTokenRollMax(rollMax);
  if (max === undefined) return seq;
  return ((seq - 1) % max) + 1;
}

/**
 * Queue labels for event orders (aligned with Mobile POS).
 * After counter reset: 1..n; older cohort: L1..Lm.
 */
export function buildEventTokenLabelById(
  orders: readonly Order[],
  tokenCounterResetAtRaw: unknown,
  eventTokenRollMaxRaw?: unknown
): Map<string, string> {
  const map = new Map<string, string>();
  const resetMs = toMillis(tokenCounterResetAtRaw);
  const rollMax = sanitizeEventTokenRollMax(eventTokenRollMaxRaw);
  const allEventOrders = orders
    .filter((o) => o.orderType === 'event')
    .slice()
    .sort(
      (a, b) =>
        orderCreatedAtMs(a) - orderCreatedAtMs(b) || String(a.id).localeCompare(String(b.id))
    );
  if (resetMs > 0) {
    let legacySeq = 0;
    let postSeq = 0;
    allEventOrders.forEach((o) => {
      const key = String(o.id);
      if (orderCreatedAtMs(o) >= resetMs) {
        postSeq += 1;
        map.set(key, String(physicalSlipFromSequence(postSeq, rollMax)));
      } else {
        legacySeq += 1;
        map.set(key, `L${legacySeq}`);
      }
    });
  } else {
    allEventOrders.forEach((order, index) => {
      map.set(String(order.id), String(physicalSlipFromSequence(index + 1, rollMax)));
    });
  }
  return map;
}

/** Prefer slip number stored on the order; otherwise derived label map (uses string order ids). */
export function getEventTokenDisplayLabel(order: Order, labelById: Map<string, string>): string | undefined {
  if (typeof order.eventQueueToken === 'number' && Number.isFinite(order.eventQueueToken) && order.eventQueueToken > 0) {
    return String(order.eventQueueToken);
  }
  return labelById.get(String(order.id));
}

/** Compact badge text: `#12` or `L3`. */
export function formatEventTokenShortBadge(label: string): string {
  if (!label) return '';
  return label.startsWith('L') ? label : `#${label}`;
}
